import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Tag,
  Filter
} from 'lucide-react';
import { partCategoriesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AllPartCategory = () => {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories for client-side pagination
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(20); // Increased from 10 to 20
  const [useClientSidePagination, setUseClientSidePagination] = useState(true); // Start with client-side pagination

  useEffect(() => {
    console.log('🔄 useEffect triggered:', {
      useClientSidePagination,
      allCategoriesLength: allCategories.length,
      currentPage
    });
    
    if (useClientSidePagination) {
      // Use client-side pagination
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = allCategories.slice(startIndex, endIndex);
      
      console.log('📊 Client-side pagination:', {
        startIndex,
        endIndex,
        paginatedDataLength: paginatedData.length,
        allCategoriesLength: allCategories.length
      });
      
      setCategories(paginatedData);
      setTotalPages(Math.ceil(allCategories.length / itemsPerPage));
      setTotalItems(allCategories.length);
    } else {
      console.log('🚀 Calling fetchCategories...');
      fetchCategories();
    }
  }, [currentPage, useClientSidePagination, allCategories]);

  // Add a separate useEffect to call fetchCategories on component mount
  useEffect(() => {
    console.log('🎯 Component mounted, calling fetchCategories...');
    
    // Check localStorage first
    console.log('🔍 Checking localStorage...');
    const token = localStorage.getItem('token');
    const adminUser = localStorage.getItem('adminUser');
    console.log('🔍 localStorage token exists:', !!token);
    console.log('🔍 localStorage adminUser exists:', !!adminUser);
    console.log('🔍 localStorage keys:', Object.keys(localStorage));
    
    // Check API configuration
    console.log('🔍 Checking API configuration...');
    console.log('🔍 partCategoriesAPI:', partCategoriesAPI);
    console.log('🔍 partCategoriesAPI.getAllSimple:', partCategoriesAPI.getAllSimple);
    console.log('🔍 partCategoriesAPI.getAll:', partCategoriesAPI.getAll);
    
    // First, let's test the API endpoint directly
    console.log('🧪 Testing API endpoint directly...');
    fetch('https://last-cot.onrender.com/api/part-categories/all')
      .then(response => {
        console.log('🔍 Direct API test response status:', response.status);
        console.log('🔍 Direct API test response ok:', response.ok);
        console.log('🔍 Direct API test response headers:', response.headers);
        return response.json();
      })
      .then(data => {
        console.log('🔍 Direct API test data:', data);
        console.log('🔍 Direct API test data type:', typeof data);
        console.log('🔍 Direct API test data is array:', Array.isArray(data));
        if (data && Array.isArray(data)) {
          console.log('🔍 Direct API test data length:', data.length);
          console.log('🔍 Direct API test first few items:', data.slice(0, 3));
        } else if (data && typeof data === 'object') {
          console.log('🔍 Direct API test data keys:', Object.keys(data));
        }
      })
      .catch(error => {
        console.log('🔍 Direct API test error:', error);
        console.log('🔍 Direct API test error message:', error.message);
        console.log('🔍 Direct API test error stack:', error.stack);
      });
    
    // Then call the main fetchCategories function
    fetchCategories();
  }, []); // Empty dependency array means this runs only once on mount

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Update pagination info when search term changes
  useEffect(() => {
    if (useClientSidePagination) {
      const filtered = allCategories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      const totalFilteredItems = filtered.length;
      const totalFilteredPages = Math.ceil(totalFilteredItems / itemsPerPage);
      
      setTotalPages(totalFilteredPages);
      setTotalItems(totalFilteredItems);
    }
  }, [searchTerm, allCategories, itemsPerPage, useClientSidePagination]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('🚀 Starting fetchCategories...');
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      const adminUser = localStorage.getItem('adminUser');
      
      console.log('🔐 Authentication check:', {
        hasToken: !!token,
        hasAdminUser: !!adminUser,
        tokenLength: token?.length || 0,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'No token',
        adminUserData: adminUser ? JSON.parse(adminUser) : null
      });
      
      // Check if token is expired
      if (token) {
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          const isExpired = tokenPayload.exp < currentTime;
          console.log('🔐 Token expiration check:', {
            exp: tokenPayload.exp,
            currentTime,
            isExpired,
            expiresIn: new Date(tokenPayload.exp * 1000).toLocaleString()
          });
          
          if (isExpired) {
            console.log('⚠️ Token is expired, clearing localStorage...');
            localStorage.removeItem('token');
            localStorage.removeItem('adminUser');
            toast.error('Session expired. Please login again.');
            window.location.href = '/admin/login';
            return;
          }
        } catch (tokenError) {
          console.log('❌ Error parsing token:', tokenError);
        }
      }
      
      if (!token || !adminUser) {
        console.log('⚠️ No authentication found, trying public API first...');
        
        // Try public API first (without authentication)
        try {
          console.log('📡 Trying public part categories API...');
          const publicResponse = await fetch('https://last-cot.onrender.com/api/part-categories/all');
          const publicData = await publicResponse.json();
          console.log('✅ Public API response:', publicData);
          
          if (publicData && Array.isArray(publicData)) {
            console.log('✅ Got part categories from public API:', publicData.length);
            setAllCategories(publicData);
            setUseClientSidePagination(true);
            setCurrentPage(1);
            setTotalItems(publicData.length);
            setTotalPages(Math.ceil(publicData.length / itemsPerPage));
            setLoading(false);
            return;
          }
        } catch (publicError) {
          console.log('❌ Public API failed:', publicError);
        }
        
        console.log('📝 Using mock data as fallback...');
        const mockCategories = [
          { _id: '1', name: 'Part Category 1' },
          { _id: '2', name: 'Part Category 2' },
          { _id: '3', name: 'Part Category 3' },
          { _id: '4', name: 'Part Category 4' },
          { _id: '5', name: 'Part Category 5' },
          { _id: '6', name: 'Part Category 6' },
          { _id: '7', name: 'Part Category 7' },
          { _id: '8', name: 'Part Category 8' },
          { _id: '9', name: 'Part Category 9' },
          { _id: '10', name: 'Part Category 10' },
          { _id: '11', name: 'Part Category 11' },
          { _id: '12', name: 'Part Category 12' }
        ];
        
        setAllCategories(mockCategories);
        setUseClientSidePagination(true);
        setCurrentPage(1);
        setTotalItems(mockCategories.length);
        setTotalPages(Math.ceil(mockCategories.length / itemsPerPage));
        setLoading(false);
        return;
      }
      
      let response;
      
      // Always try to get all part categories first using getAllSimple
      try {
        console.log('📡 Fetching all part categories using getAllSimple...');
        console.log('API URL:', 'https://last-cot.onrender.com/api/part-categories/all');
        response = await partCategoriesAPI.getAllSimple();
        console.log('✅ Part Categories getAllSimple response:', response);
        console.log('✅ Response data:', response.data);
        console.log('✅ Response status:', response.status);
      } catch (simpleError) {
        console.log('❌ getAllSimple failed:', simpleError);
        console.log('❌ Error response:', simpleError.response);
        console.log('❌ Error status:', simpleError.response?.status);
        console.log('❌ Error data:', simpleError.response?.data);
        
        console.log('🔄 Trying getAll without params...');
        try {
          response = await partCategoriesAPI.getAll();
          console.log('✅ Part Categories getAll response:', response);
          console.log('✅ Response data:', response.data);
        } catch (getAllError) {
          console.log('❌ getAll also failed:', getAllError);
          console.log('🔄 Trying getAll with empty params...');
          response = await partCategoriesAPI.getAll({});
          console.log('✅ Part Categories getAll with empty params response:', response);
          console.log('✅ Response data:', response.data);
        }
      }
      
      // Handle different possible API response structures
      let categoriesData = [];
      
      console.log('🔍 Processing response data...');
      console.log('🔍 Response exists:', !!response);
      console.log('🔍 Response.data exists:', !!(response && response.data));
      
      if (response && response.data) {
        console.log('🔍 Response.data type:', typeof response.data);
        console.log('🔍 Response.data is array:', Array.isArray(response.data));
        console.log('🔍 Response.data keys:', response.data ? Object.keys(response.data) : 'No keys');
        
        // Check for direct array: response.data
        if (Array.isArray(response.data)) {
          categoriesData = response.data;
          console.log('✅ Found direct array, categories count:', categoriesData.length);
        }
        // Check for success response with data property
        else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          categoriesData = response.data.data;
          console.log('✅ Found success.data array, categories count:', categoriesData.length);
        }
        // Check for direct array: response.data.partCategories
        else if (response.data.partCategories && Array.isArray(response.data.partCategories)) {
          categoriesData = response.data.partCategories;
          console.log('✅ Found partCategories array, categories count:', categoriesData.length);
        }
        // Check for paginated response structure
        else if (response.data.data && response.data.data.partCategories) {
          categoriesData = response.data.data.partCategories;
          console.log('✅ Found paginated partCategories array, categories count:', categoriesData.length);
        }
        else {
          console.log('❌ No recognized data structure found');
          console.log('❌ Available data structure:', JSON.stringify(response.data, null, 2));
        }
      } else {
        console.log('❌ No response or response.data available');
      }
      
      console.log('Extracted categories data:', categoriesData);
      console.log('Categories data details:', {
        length: categoriesData.length,
        firstCategory: categoriesData[0],
        lastCategory: categoriesData[categoriesData.length - 1],
        allCategoryNames: categoriesData.slice(0, 5).map(c => c.name) // Show first 5 category names
      });
      
      // Always use client-side pagination for all categories
      if (categoriesData.length > 0) {
        console.log('🎉 Setting up client-side pagination for', categoriesData.length, 'categories');
        
        // Store all categories and enable client-side pagination
        setAllCategories(categoriesData);
        setUseClientSidePagination(true);
        setTotalItems(categoriesData.length);
        setTotalPages(Math.ceil(categoriesData.length / itemsPerPage));
        
        // Apply pagination to current page
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = categoriesData.slice(startIndex, endIndex);
        
        console.log('✅ Client-side pagination applied:', {
          startIndex,
          endIndex,
          paginatedData: paginatedData.length,
          totalItems: categoriesData.length,
          totalPages: Math.ceil(categoriesData.length / itemsPerPage)
        });
        
        setCategories(paginatedData);
      } else {
        console.log('⚠️ No categories data found, trying fallback...');
        
        // Try direct fetch as fallback
        try {
          console.log('🔄 Trying direct fetch as fallback...');
          const fallbackResponse = await fetch('https://last-cot.onrender.com/api/part-categories/all');
          const fallbackData = await fallbackResponse.json();
          console.log('✅ Fallback response:', fallbackData);
          
          if (fallbackData && Array.isArray(fallbackData) && fallbackData.length > 0) {
            console.log('🎉 Got categories from fallback:', fallbackData.length);
            setAllCategories(fallbackData);
            setUseClientSidePagination(true);
            setTotalItems(fallbackData.length);
            setTotalPages(Math.ceil(fallbackData.length / itemsPerPage));
            
            // Apply pagination to current page
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedData = fallbackData.slice(startIndex, endIndex);
            setCategories(paginatedData);
          } else {
            console.log('❌ Fallback also failed, setting empty state');
            setCategories([]);
            setAllCategories([]);
            setTotalItems(0);
            setTotalPages(1);
          }
        } catch (fallbackError) {
          console.log('❌ Fallback fetch failed:', fallbackError);
          setCategories([]);
          setAllCategories([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error('💥 Failed to fetch categories:', error);
      console.error('💥 Error response:', error.response?.data);
      console.error('💥 Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        console.log('🔐 Authentication failed, trying direct fetch...');
        toast.error('Authentication required. Trying alternative method...');
        
        // Try direct fetch without axios
        try {
          console.log('🔄 Trying direct fetch after auth failure...');
          const directResponse = await fetch('https://last-cot.onrender.com/api/part-categories/all');
          const directData = await directResponse.json();
          console.log('✅ Direct fetch response:', directData);
          
          if (directData && Array.isArray(directData) && directData.length > 0) {
            console.log('🎉 Got categories from direct fetch:', directData.length);
            setAllCategories(directData);
            setUseClientSidePagination(true);
            setTotalItems(directData.length);
            setTotalPages(Math.ceil(directData.length / itemsPerPage));
            setCurrentPage(1);
            
            // Apply pagination to current page
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedData = directData.slice(startIndex, endIndex);
            setCategories(paginatedData);
            return; // Exit early on success
          }
        } catch (directError) {
          console.log('❌ Direct fetch also failed:', directError);
        }
        
        // Try to use cached data if available
        if (allCategories.length > 0) {
          console.log('📦 Using cached categories data:', allCategories.length);
          setUseClientSidePagination(true);
          setCurrentPage(1);
        } else {
          // Use mock data for testing
          const mockCategories = [
            { _id: '1', name: 'Part Category 1' },
            { _id: '2', name: 'Part Category 2' },
            { _id: '3', name: 'Part Category 3' },
            { _id: '4', name: 'Part Category 4' },
            { _id: '5', name: 'Part Category 5' },
            { _id: '6', name: 'Part Category 6' },
            { _id: '7', name: 'Part Category 7' },
            { _id: '8', name: 'Part Category 8' },
            { _id: '9', name: 'Part Category 9' },
            { _id: '10', name: 'Part Category 10' },
            { _id: '11', name: 'Part Category 11' },
            { _id: '12', name: 'Part Category 12' }
          ];
          
          console.log('📝 Using mock categories data for testing:', mockCategories.length);
          setAllCategories(mockCategories);
          setUseClientSidePagination(true);
          setCurrentPage(1);
          setTotalItems(mockCategories.length);
          setTotalPages(Math.ceil(mockCategories.length / itemsPerPage));
        }
      } else if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view part categories.');
        setCategories([]);
      } else {
        console.log('🔄 Trying direct fetch after general error...');
        // Try direct fetch as last resort
        try {
          const lastResortResponse = await fetch('https://last-cot.onrender.com/api/part-categories/all');
          const lastResortData = await lastResortResponse.json();
          console.log('✅ Last resort response:', lastResortData);
          
          if (lastResortData && Array.isArray(lastResortData) && lastResortData.length > 0) {
            console.log('🎉 Got categories from last resort:', lastResortData.length);
            setAllCategories(lastResortData);
            setUseClientSidePagination(true);
            setTotalItems(lastResortData.length);
            setTotalPages(Math.ceil(lastResortData.length / itemsPerPage));
            setCurrentPage(1);
            
            // Apply pagination to current page
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedData = lastResortData.slice(startIndex, endIndex);
            setCategories(paginatedData);
            return; // Exit early on success
          }
        } catch (lastResortError) {
          console.log('❌ Last resort also failed:', lastResortError);
        }
        
        toast.error('Failed to load part categories. Please check your connection.');
        setCategories([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        // Update existing category
        await partCategoriesAPI.update(editingCategory._id, formData);
        toast.success('Category updated successfully');
      } else {
        // Add new category using the API
        await partCategoriesAPI.create(formData);
        toast.success('Category added successfully');
      }
      
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '' });
      
      // Reset pagination and fetch fresh data
      setCurrentPage(1);
      setUseClientSidePagination(true);
      setAllCategories([]);
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name
    });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
      try {
        await partCategoriesAPI.delete(category._id);
        toast.success('Category deleted successfully');
        
        // Reset pagination and fetch fresh data
        setCurrentPage(1);
        setUseClientSidePagination(true);
        setAllCategories([]);
        fetchCategories();
      } catch (error) {
        console.error('Failed to delete category:', error);
        toast.error('Failed to delete category');
      }
    }
  };

  // Handle search with client-side pagination
  const getFilteredCategories = () => {
    if (useClientSidePagination) {
      const filtered = allCategories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      return filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    } else {
      return categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  };

  const filteredCategories = getFilteredCategories();
  
  // Debug logging
  console.log('Current state:', {
    useClientSidePagination,
    allCategoriesCount: allCategories.length,
    categoriesCount: categories.length,
    filteredCategoriesCount: filteredCategories.length,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    allCategories: allCategories.slice(0, 3) // Show first 3 categories for debugging
  });

  // Pagination functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#F5F1EB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EB] p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <img 
              src="/logo.svg" 
              alt="COT Logo" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-3xl font-bold text-secondary font-wix">All Part Categories</h1>
              <p className="text-secondary/70 font-wix">Manage part categories and classifications</p>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Category</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary/40" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent font-wix"
            />
          </div>
        </div>
      </div>

      {/* Categories Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F5F1EB]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-secondary font-wix">
                  Category Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-secondary font-wix">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCategories.map((category) => (
                <tr key={category._id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="bg-primary/10 p-2 rounded-xl">
                        <Tag className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-secondary font-wix">
                          {category.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-primary hover:text-primary/80 p-2 hover:bg-primary/10 rounded-xl transition-colors duration-300"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-xl transition-colors duration-300"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 font-wix">
                  Showing{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalItems)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{totalItems}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary border-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-primary/10 p-6 rounded-2xl inline-block mb-6">
              <Tag className="h-16 w-16 text-primary mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-secondary font-wix mb-2">No Categories Found</h3>
            <p className="text-secondary/70 font-wix mb-6">
              {searchTerm ? 'No categories match your search criteria' : 'Get started by creating your first category'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Plus className="h-5 w-5" />
                <span>Add First Category</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-secondary font-wix mb-6">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-secondary font-wix mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent font-wix"
                  placeholder="Enter category name"
                />
              </div>
              
              
              <div className="flex items-center space-x-4 pt-4">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: '' });
                  }}
                  className="px-6 py-3 text-secondary border border-gray-200 rounded-xl hover:bg-gray-50 font-wix transition-colors duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllPartCategory;
